import React from 'react';
import { billingFeaturesData } from '../../data/distributorsData';
import { Target, Zap, Link, Shield, CheckCircle } from 'lucide-react';

const iconMap = {
  Target,
  Zap,
  Link,
  Shield
};

const BillingSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
              Facturación Automatizada
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Maximiza precisión en facturación y{' '}
              <span className="text-[#1297fd]">protección de ingresos</span>
            </h2>
            <p className="text-lg text-[#c3c3c5] mb-8 leading-relaxed">
              Mejora el control financiero con facturación automatizada, cobros acelerados, errores de facturación reducidos y mayor transparencia en cada transacción.
            </p>

            {/* Features List */}
            <div className="space-y-6">
              {billingFeaturesData.map((feature) => {
                const IconComponent = iconMap[feature.icon];
                return (
                  <div key={feature.id} className="flex gap-4 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                      {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#1297fd] transition-colors">
                        {feature.title}
                      </h4>
                      <p className="text-[#c3c3c5] text-sm leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl">
              {/* Billing Dashboard Mock */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-white font-semibold">Panel de Facturación</span>
                <span className="text-green-400 text-sm">En tiempo real</span>
              </div>

              {/* Accuracy Meter */}
              <div className="bg-white/5 rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-white">Precisión de Facturación</span>
                  <span className="text-[#40aaf8] font-bold text-2xl">99.98%</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full w-[99.98%] rounded-full" style={{ background: 'linear-gradient(to right, #1297fd, #40aaf8)' }} />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-white font-bold text-xl">$124.5K</p>
                  <p className="text-[#8bcaf9] text-xs">Facturado hoy</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-white font-bold text-xl">2,847</p>
                  <p className="text-[#8bcaf9] text-xs">Facturas procesadas</p>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="space-y-3">
                {[
                  { name: 'Microsoft 365 - Reseller A', amount: '$2,450', status: 'Completado' },
                  { name: 'Adobe CC - Reseller B', amount: '$1,200', status: 'Procesando' },
                  { name: 'AWS Services - Reseller C', amount: '$3,800', status: 'Completado' }
                ].map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className={`w-4 h-4 ${tx.status === 'Completado' ? 'text-green-400' : 'text-[#8bcaf9]'}`} />
                      <span className="text-white/80 text-sm">{tx.name}</span>
                    </div>
                    <span className="text-white font-medium text-sm">{tx.amount}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Decorative */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#8bcaf9]/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#1297fd]/20 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BillingSection;
